// @flow
import * as React from 'react';
import Box from 'sail/Box';
import Button from 'sail/Button';
import {ContentBlock, ContentHeader} from 'sail/Content';
import {Grid, GridItem} from 'sail/Grid';
import Section from 'sail/Section';
import {BodyAlt, Subheading} from 'sail/Text';
import {AttentionNotice} from 'sail/Notice';
import {TextInput} from 'sail/Input';
import {arrowRight} from 'sail/Icon/Assets';
import DataTable, {
  DataTableBody,
  DataTableCell,
  DataTableRow,
} from 'sail/DataTable';

type SamplePadAgreementProps = {|
  children: React.Node,
|};

export const SamplePadAgreement = ({children}: SamplePadAgreementProps) => {
  return (
    <Box>
      <Section>
        <ContentHeader
          style={{textAlign: 'center'}}
          size="large"
          title="Rocket Rides"
          divider={false}
        />
        <ContentBlock padding={12} divider={false}>
          123 First Avenue <br />
          Toronto, ON <br />
          M2J 3R7 <br />
          Email: billing@rocket-rides.com <br />
          <ContentHeader
            size="medium"
            title="Pre-authorized debit agreement"
            divider={false}
            padding={{left: 0, top: 20}}
          />
          <br />
          <DataTable>
            <DataTableBody>
              <DataTableRow>
                <DataTableCell vAlign="middle">
                  <Subheading>Date</Subheading>
                </DataTableCell>
                <DataTableCell vAlign="middle">April 30, 2020</DataTableCell>
                <DataTableCell vAlign="middle">
                  <Subheading>Type</Subheading>
                </DataTableCell>
                <DataTableCell vAlign="middle">Business</DataTableCell>
              </DataTableRow>
              <DataTableRow>
                <DataTableCell vAlign="middle">
                  <Subheading>Account Holder</Subheading>
                </DataTableCell>
                <DataTableCell vAlign="middle">
                  <TextInput placeholder="Jenny Rosen" />
                </DataTableCell>
                <DataTableCell vAlign="middle">
                  <Subheading>Email</Subheading>
                </DataTableCell>
                <DataTableCell vAlign="middle">
                  <TextInput placeholder="jrosen@example.com" />
                </DataTableCell>
              </DataTableRow>
              <DataTableRow>
                <DataTableCell vAlign="middle">
                  <Subheading>Institution</Subheading>
                </DataTableCell>
                <DataTableCell vAlign="middle">
                  <TextInput placeholder="001" />
                </DataTableCell>
                <DataTableCell vAlign="middle">
                  <Subheading>Transit</Subheading>
                </DataTableCell>
                <DataTableCell vAlign="middle">
                  <TextInput placeholder="12345" />
                </DataTableCell>
              </DataTableRow>
              <DataTableRow>
                <DataTableCell vAlign="middle">
                  <Subheading>Account #</Subheading>
                </DataTableCell>
                <DataTableCell vAlign="middle">
                  <TextInput placeholder="000123456789" />
                </DataTableCell>
                <DataTableCell vAlign="middle" />
                <DataTableCell vAlign="middle" />
              </DataTableRow>
            </DataTableBody>
          </DataTable>
          <br />
          <p>
            By clicking submit, you accept this Agreement and authorize{' '}
            <em>Rocket Rides</em> to debit the specified bank account for any
            amount owed for charges arising from the use of services and/or
            purchase of products.
          </p>
          <br />
          <Grid gap={12}>
            <GridItem columnSpan={6}>
              <Box flex={{justifyContent: 'center'}} style={{height: '100%'}}>
                <p>
                  Payments will be debited from the specified account according
                  to the following schedule:
                  <br />
                  <code>On the 5th of every month</code>
                </p>
              </Box>
            </GridItem>
            <GridItem column={7} columnSpan={6}>
              <AttentionNotice>
                <p>
                  Displaying payment schedules or triggers is only required if{' '}
                  <code>payment_schedule</code> is <code>interval</code> or{' '}
                  <code>combined</code>
                </p>
              </AttentionNotice>
            </GridItem>
            <GridItem row={2} columnSpan={6}>
              <p>
                One-time, sporadic payments may be debited from the specified
                account by <em>Rocket Rides</em> only after your authorization
                is obtained.
              </p>
            </GridItem>
            <GridItem row={2} column={7} columnSpan={6}>
              <AttentionNotice>
                <p>
                  Displaying terms for sporadic payments is only required if{' '}
                  <code>payment_schedule</code> is <code>sporadic</code> or{' '}
                  <code>combined</code>
                </p>
              </AttentionNotice>
            </GridItem>
          </Grid>
          <br />
          <p>
            Where a scheduled debit date is not a business day,{' '}
            <em>Rocket Rides</em> will debit on the next business day.
          </p>
          <p>
            You agree to receive confirmation of this agreement at least 3
            (three) days before the initial payment due date.{' '}
            <BodyAlt>
              You further agree to be notified of upcoming debits up to 1 (one)
              day before payments are collected.
            </BodyAlt>
          </p>
          <p>
            You have certain recourse rights if any debit does not comply with
            this agreement. For example, you have the right to receive
            reimbursement for any debit that is not authorized or is not
            consistent with this PAD Agreement. To obtain more information on
            your recourse rights, contact your financial institution or visit{' '}
            <a data-no-arrow href="https://www.payments.ca">
              www.payments.ca
            </a>
            .
          </p>
          <p>
            You may amend or cancel this authorization at any time by providing
            the merchant with thirty (30) days notice at
            billing@rocket-rides.com. To obtain a sample cancellation form, or
            further information on cancelling a PAD agreement, please contact
            your financial institution or visit{' '}
            <a data-no-arrow href="https://www.payments.ca">
              www.payments.ca
            </a>
            .
          </p>
          <p>
            <em>Rocket Rides</em> partners with{' '}
            <a data-no-arrow href="/">
              Stripe
            </a>{' '}
            to provide payment processing.
          </p>
          <br />
          <Box flex={{justifyContent: 'flex-end'}}>
            <Button
              color="blue"
              icon={arrowRight}
              iconPosition="right"
              label="Submit"
            />
          </Box>
        </ContentBlock>
      </Section>
    </Box>
  );
};
